import { useEffect, useRef, useState, useCallback } from "react";
import confetti from "canvas-confetti";
import { Heart, Sparkles, Check } from "lucide-react";

/**
 * Per-prospect "Darke discount" easter egg.
 * Sloan (Kim Darke) + Dykema (anyone named Darke) get a hidden 25% bump
 * when they answer "Yes" to the question.
 *
 * State is intentionally NON-PERSISTENT: it lives in module memory and
 * resets on every page load. That way every fresh viewer of the deck
 * gets the click → confetti experience cleanly. State still propagates
 * across slides within the same visit via a window-level event.
 */
const _activated = new Set();
const EVENT_NAME = "darke-discount-change";

export function useDarkeDiscount(slug) {
  const read = useCallback(() => _activated.has(slug || "anon"), [slug]);

  const [active, setActive] = useState(read);

  useEffect(() => {
    setActive(read());
    const onChange = () => setActive(read());
    window.addEventListener(EVENT_NAME, onChange);
    return () => window.removeEventListener(EVENT_NAME, onChange);
  }, [read]);

  const activate = useCallback(() => {
    if (typeof window === "undefined") return;
    _activated.add(slug || "anon");
    window.dispatchEvent(new Event(EVENT_NAME));
    setActive(true);
  }, [slug]);

  return [active, activate];
}

/** Fire a multi-burst confetti volley from both lower corners. */
function fireFireworks() {
  const duration = 2200;
  const end = Date.now() + duration;
  const colors = ["#F5B400", "#E07A2B", "#FFE08A", "#fff", "#A86A1E"];

  (function frame() {
    confetti({
      particleCount: 5,
      angle: 60,
      spread: 70,
      origin: { x: 0, y: 0.85 },
      colors,
      startVelocity: 55,
      scalar: 0.9,
    });
    confetti({
      particleCount: 5,
      angle: 120,
      spread: 70,
      origin: { x: 1, y: 0.85 },
      colors,
      startVelocity: 55,
      scalar: 0.9,
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();

  // Big center burst
  setTimeout(() => {
    confetti({
      particleCount: 140,
      spread: 110,
      origin: { x: 0.5, y: 0.5 },
      colors,
      startVelocity: 45,
      scalar: 1.1,
    });
  }, 250);
  setTimeout(() => {
    confetti({
      particleCount: 100,
      spread: 160,
      origin: { x: 0.5, y: 0.4 },
      colors,
      startVelocity: 35,
      scalar: 1.0,
    });
  }, 900);
}

export function DarkeDiscountCallout({
  slug,
  question = "Do you know a Darke?",
  hint = "(humor us — there might be a discount in it)",
  unlockedHeadline = "Darke Discount unlocked",
  unlockedSub = "25% off the founding rate, locked in for the first 12 months.",
  noResponseText = "No worries — pricing above stands.",
  testId = "darke-discount",
}) {
  const [active, activate] = useDarkeDiscount(slug);
  const [declined, setDeclined] = useState(false);
  const firedRef = useRef(false);

  const onYes = () => {
    if (!firedRef.current) {
      firedRef.current = true;
      fireFireworks();
    }
    activate();
  };

  if (active) {
    return (
      <div
        className="border-2 border-sun-500 rounded-md p-4 bg-gradient-to-br from-sun-500/15 via-ember-500/10 to-sun-500/15 animate-fade-in"
        data-testid={`${testId}-active`}
      >
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-full bg-sun-500/25 border border-sun-500/50 grid place-items-center flex-shrink-0">
            <Sparkles className="h-5 w-5 text-sun-700" strokeWidth={1.8} />
          </div>
          <div className="flex-1">
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700 flex items-center gap-1.5">
              <Heart className="h-3 w-3 fill-ember-500 text-ember-500" /> Family rate
            </div>
            <div className="serif text-lg text-ink-900 leading-snug mt-0.5">
              {unlockedHeadline} <em className="italic text-sun-700">— 25% off.</em>
            </div>
            <p className="text-sm text-ink-900/75 mt-1 leading-relaxed">{unlockedSub}</p>
          </div>
          <div className="mono text-[9px] uppercase tracking-[0.22em] bg-sun-500 text-ink-900 rounded-full px-2.5 py-1 flex items-center gap-1">
            <Check className="h-3 w-3" /> 25% locked
          </div>
        </div>
      </div>
    );
  }

  if (declined) {
    return (
      <div
        className="border border-ink-900/12 rounded-md p-4 bg-white/60"
        data-testid={`${testId}-declined`}
      >
        <p className="text-sm text-ink-900/65 italic">{noResponseText}</p>
      </div>
    );
  }

  return (
    <div
      className="border border-sun-500/40 rounded-md p-4 bg-gradient-to-br from-sun-500/8 to-transparent"
      data-testid={testId}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="h-10 w-10 rounded-full bg-sun-500/15 border border-sun-500/40 grid place-items-center flex-shrink-0">
          <Heart className="h-5 w-5 text-ember-600" strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="serif text-lg text-ink-900 leading-snug">{question}</div>
          <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 mt-1">
            {hint}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onYes}
            data-testid={`${testId}-yes`}
            className="mono text-[11px] uppercase tracking-[0.22em] bg-ink-900 hover:bg-ink-800 text-cream rounded-md px-4 py-2 transition-colors"
          >
            Yes
          </button>
          <button
            type="button"
            onClick={() => setDeclined(true)}
            data-testid={`${testId}-no`}
            className="mono text-[11px] uppercase tracking-[0.22em] border border-ink-900/20 text-ink-900/70 hover:border-ink-900/40 hover:text-ink-900 rounded-md px-4 py-2 transition-colors"
          >
            No
          </button>
        </div>
      </div>
    </div>
  );
}
